import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Button, Popconfirm, Space, Input, Modal, Form, Select,
  Typography, Switch, Card, message, InputNumber, Tooltip, Badge,
  Row, Col, Statistic, Avatar
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined,
  ReloadOutlined, UserOutlined
} from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const colorOptions = [
  { value: '#1890ff', label: '蓝色' },
  { value: '#52c41a', label: '绿色' },
  { value: '#faad14', label: '黄色' },
  { value: '#f5222d', label: '红色' },
  { value: '#722ed1', label: '紫色' },
  { value: '#13c2c2', label: '青色' },
  { value: '#fa8c16', label: '橙色' },
  { value: '#eb2f96', label: '粉色' },
];

function UserTagsPage() {
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadTags = async () => {
    setLoading(true);
    try {
      const { list, total: t } = await api.get('/admin/user-tags');
      setTags(list || []);
      setTotal(t || list?.length || 0);
    } catch (e) {
      message.error('加载标签失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTags(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/admin/user-tags', values);
      message.success('标签创建成功');
      setCreateModal(false);
      form.resetFields();
      loadTags();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : '创建失败');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue(record);
    setEditModal(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await api.put(`/admin/user-tags/${editingRecord.id}`, values);
      message.success('标签更新成功');
      setEditModal(false);
      loadTags();
    } catch (e) {
      message.error('更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/user-tags/${id}`);
      message.success('标签已删除');
      loadTags();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      await api.put(`/admin/user-tags/${id}`, { status: currentStatus === 1 ? 0 : 1 });
      message.success(currentStatus === 1 ? '已禁用' : '已启用');
      loadTags();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '标签名称', width: 180,
      render: (_, r) => (
        <Space>
          <Tag color={r.color || '#1890ff'}>{r.name}</Tag>
        </Space>
      ),
    },
    {
      title: '颜色', width: 100,
      render: (_, r) => (
        <Space>
          <div style={{ width: 20, height: 20, borderRadius: 4, background: r.color || '#1890ff' }} />
          <Text type="secondary">{r.color || '#1890ff'}</Text>
        </Space>
      ),
    },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '排序', width: 80,
      align: 'center',
      render: (_, r) => <Text>{r.sort_order || 0}</Text>,
    },
    {
      title: '状态', width: 100,
      render: (_, r) => (
        <Switch
          size="small"
          checked={r.status === 1}
          onChange={() => handleStatusToggle(r.id, r.status)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 160, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 150,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该标签?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <TagsOutlined /> 用户标签管理
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadTags}>刷新</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { form.resetFields(); setCreateModal(true); }}
            style={{ background: '#07C160', borderColor: '#07C160' }}
          >
            新增标签
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="标签总数" value={tags.length} prefix={<TagsOutlined style={{ color: '#1890ff' }} />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="启用中"
              value={tags.filter(t => t.status === 1).length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="已禁用"
              value={tags.filter(t => t.status !== 1).length}
              valueStyle={{ color: '#bfbfbf' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Table
          columns={columns}
          dataSource={tags}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
        />
      </Card>

      {/* 创建标签弹窗 */}
      <Modal
        title={<span><PlusOutlined /> 新增标签</span>}
        open={createModal}
        onOk={handleCreate}
        onCancel={() => { setCreateModal(false); form.resetFields(); }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="标签名称" rules={[{ required: true, message: '请输入标签名称' }]}>
            <Input placeholder="如: VIP用户" />
          </Form.Item>
          <Form.Item name="color" label="标签颜色" initialValue="#1890ff">
            <Select placeholder="选择颜色">
              {colorOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>
                  <Space>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: o.value }} />
                    {o.label}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="标签描述信息" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值" initialValue={0}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑标签弹窗 */}
      <Modal
        title={<span><EditOutlined /> 编辑标签 - {editingRecord?.name || ''}</span>}
        open={editModal}
        onOk={handleEditSave}
        onCancel={() => { setEditModal(false); setEditingRecord(null); editForm.resetFields(); }}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="标签名称" rules={[{ required: true, message: '请输入标签名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="color" label="标签颜色">
            <Select placeholder="选择颜色">
              {colorOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>
                  <Space>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: o.value }} />
                    {o.label}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default UserTagsPage;
